import React from "react";
import { Typography, Button, Box, CircularProgress, Alert } from "@mui/material";
import { ResponsiveModal } from "@answersnow/pkg-theme";
import { ANUploader } from "@answersnow/pkg-theme";
import ImageCropper from "./ImageCropper";
import useANUploader from "../../../hooks/utils/useANUploader";
import { useUploadUserAvatar } from "../../../hooks/api/user/useUploadUserAvatar";
import { Area } from "react-easy-crop/types";

interface AvatarEditProps {
  userId: number;
  currentImageUrl?: string;
  onSuccess?: (imageUrl: string) => void;
  disabled?: boolean;
}

interface CropData {
  x: number;
  y: number;
  width: number;
  height: number;
  zoom: number;
}

const parseCropParams = (url: string): { baseUrl: string; cropData: CropData | null } => {
  if (!url) return { baseUrl: url, cropData: null };

  const [baseUrl, cropString] = url.split("?crop=");

  if (!cropString) {
    return { baseUrl, cropData: null };
  }

  const [x, y, width, height, zoom] = cropString.split(",").map(Number);

  return {
    baseUrl,
    cropData: { x, y, width, height, zoom },
  };
};

const AvatarEdit: React.FC<AvatarEditProps> = ({
  userId,
  currentImageUrl,
  onSuccess,
  disabled = false,
}) => {
  const [open, setOpen] = React.useState(false);
  const [uploadError, setUploadError] = React.useState<string | null>(null);
  const [croppedAreaPixels, setCroppedAreaPixels] = React.useState<Area | null>(null);
  const [imagePreview, setImagePreview] = React.useState<string | null>(null);
  const [loadingImage, setLoadingImage] = React.useState(false);
  const [skipCurrentImage, setSkipCurrentImage] = React.useState(false);
  const [initialCropData, setInitialCropData] = React.useState<CropData | null>(null);
  const [currentZoom, setCurrentZoom] = React.useState(1);
  const [modalKey, setModalKey] = React.useState(0); // Add version counter
  const { dropzoneFiles, setDropzoneFiles, handleFileDelete, handleFileUpload } =
    useANUploader(false);

  React.useEffect(() => {
    if (
      open &&
      currentImageUrl &&
      dropzoneFiles.length === 0 &&
      !imagePreview &&
      !skipCurrentImage
    ) {
      setLoadingImage(true);

      // Remove timestamp parameter if present (handle both ?t= and &t=)
      let urlWithoutTimestamp = currentImageUrl.split("?t=")[0].split("&t=")[0];
      const { baseUrl, cropData } = parseCropParams(urlWithoutTimestamp);

      if (cropData) {
        setInitialCropData(cropData);
        setCurrentZoom(cropData.zoom);
      }

      const img = new Image();
      img.crossOrigin = "anonymous";

      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");
        if (ctx) {
          ctx.drawImage(img, 0, 0);
          canvas.toBlob((blob) => {
            if (blob) {
              const url = URL.createObjectURL(blob);
              setImagePreview(url);
            }
            setLoadingImage(false);
          }, "image/jpeg");
        } else {
          setLoadingImage(false);
          setSkipCurrentImage(true);
        }
      };

      img.onerror = () => {
        console.error("Failed to load image with CORS");
        setLoadingImage(false);
        setSkipCurrentImage(true);
      };

      img.src = baseUrl;
    }
  }, [open, currentImageUrl, dropzoneFiles.length, imagePreview, skipCurrentImage, modalKey]);

  // Handle file upload from dropzone
  React.useEffect(() => {
    if (dropzoneFiles.length > 0 && dropzoneFiles[0].file) {
      const file = dropzoneFiles[0].file;
      const objectUrl = URL.createObjectURL(file);
      setImagePreview(objectUrl);
    }
  }, [dropzoneFiles]);

  const { uploadAvatar, isUploading, isSuccess, isError } = useUploadUserAvatar({
    onSuccess: (data) => {
      setUploadError(null);
      onSuccess?.(data.imageUrl);
      handleClose();
    },
    onError: (error: any) => {
      if (error?.response?.status === 413) {
        setUploadError("Image is too large. Please select a smaller image.");
      } else if (error?.message) {
        setUploadError(error.message);
      } else {
        setUploadError("Failed to upload avatar. Please try again.");
      }
    },
  });

  const handleOpen = () => {
    // Reset all state when opening and increment key to force reload
    setImagePreview(null);
    setInitialCropData(null);
    setCurrentZoom(1);
    setSkipCurrentImage(false);
    setCroppedAreaPixels(null);
    setDropzoneFiles([]);
    setUploadError(null);
    setModalKey((prev) => prev + 1); // Increment to force useEffect to run
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setDropzoneFiles([]);
    setUploadError(null);
    setImagePreview(null);
    setCroppedAreaPixels(null);
    setInitialCropData(null);
    setCurrentZoom(1);
    setLoadingImage(false);
    setSkipCurrentImage(false);
  };

  const handleCropComplete = (croppedArea: Area, zoom: number) => {
    setCroppedAreaPixels(croppedArea);
    setCurrentZoom(zoom);
  };

  const handleSave = async () => {
    if (!imagePreview || !croppedAreaPixels) return;

    try {
      const originalBlob = await fetch(imagePreview).then((r) => r.blob());

      const fileName =
        dropzoneFiles.length > 0 && dropzoneFiles[0].file
          ? dropzoneFiles[0].file.name
          : "avatar.jpg";

      const originalFile = new File([originalBlob], fileName, { type: "image/jpeg" });

      const { x, y, width, height } = croppedAreaPixels;
      const cropParams = `${x},${y},${width},${height},${currentZoom}`;

      setUploadError(null);
      uploadAvatar({ userId, file: originalFile, cropParams });
    } catch (error) {
      console.error("Upload error:", error);
      setUploadError("Failed to upload image. Please try again.");
    }
  };

  const handleChangeImage = () => {
    setDropzoneFiles([]);
    setImagePreview(null);
    setCroppedAreaPixels(null);
    setInitialCropData(null);
    setCurrentZoom(1);
    setSkipCurrentImage(true);
  };

  const showCropper = imagePreview !== null && !loadingImage;
  const showUploader = !loadingImage && !showCropper;

  return (
    <>
      <Typography
        variant="body1"
        onClick={disabled ? undefined : handleOpen}
        sx={{
          cursor: disabled ? "default" : "pointer",
        }}
      >
        Edit
      </Typography>

      <ResponsiveModal open={open} onClose={handleClose} title="Edit Profile Picture" maxWidth="sm">
        <Box sx={{ pt: 2 }}>
          {loadingImage && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: 300,
              }}
            >
              <CircularProgress />
            </Box>
          )}

          {showUploader && (
            <ANUploader
              handleFileUpload={handleFileUpload}
              handleFileDelete={handleFileDelete}
              dropzoneFiles={dropzoneFiles}
              useDropzoneOptions={{
                multiple: false,
                accept: "image/*",
              }}
              circularMask={false}
            />
          )}

          {showCropper && (
            <Box>
              <ImageCropper
                imageSrc={imagePreview}
                onCropComplete={handleCropComplete}
                initialZoom={initialCropData?.zoom}
              />
            </Box>
          )}

          {uploadError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {uploadError}
            </Alert>
          )}

          {isSuccess && (
            <Alert severity="success" sx={{ mt: 2 }}>
              Avatar uploaded successfully!
            </Alert>
          )}

          {showCropper && (
            <Box sx={{ display: "flex", justifyContent: "center", pt: 3, gap: 3 }}>
              <Button
                color="secondary"
                onClick={handleChangeImage}
                disabled={isUploading}
                sx={{ width: "100%" }}
              >
                Change Image
              </Button>

              <Button
                variant="contained"
                onClick={handleSave}
                disabled={!croppedAreaPixels || isUploading}
                sx={{ width: "100%" }}
                startIcon={isUploading ? <CircularProgress size={20} color="inherit" /> : null}
              >
                {isUploading ? "Uploading..." : "Save Changes"}
              </Button>
            </Box>
          )}
        </Box>
      </ResponsiveModal>
    </>
  );
};

export default AvatarEdit;
